import { ChangeEvent, Fragment, useState } from "react";
import { useIntl } from "react-intl";
import { Clear } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { useAccount } from "wagmi";
import { pad, toHex } from "viem";

import { EntityInput } from "@dzambalaorg/mui-inputs-entity";
import { StaticInput } from "@dzambalaorg/mui-inputs-core";
import { FormWrapper } from "@dzambalaorg/mui-form";
import { useMetamask, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { AddressType, CompositionStatus, IBalance, IToken, TokenType } from "@framework/types";
import ERC998SimpleSafeTransferChildABI from "@framework/abis/json/ERC998Simple/safeTransferChild.json";
import ERC1155SimpleSafeTransferFrom1155ABI from "@framework/abis/json/ERC1155Simple/safeTransferFrom.json";
import ERC721SimpleSafeTransferFrom721ABI from "@framework/abis/json/ERC721Simple/safeTransferFrom.json";
import ERC998ERC1155ERC20EnumSafeTransferFromERC1155ABI from "@framework/abis/json/ERC998ERC1155ERC20Enum/safeTransferFromERC1155.json";
import ERC998ERC1155ERC20EnumTransferERC20ABI from "@framework/abis/json/ERC998ERC1155ERC20Enum/transferERC20.json";
import ERC998ERC1155ERC20EnumGetERC20ABI from "@framework/abis/json/ERC998ERC1155ERC20Enum/getERC20.json";
import { Button, Typography } from "@dzambalaorg/ui-kit";

import { formatTokenTitle } from "../../../../../utils/token";
import { ComposeTokenDialog, IComposeTokenDto } from "./dialog";
import { StyledInputAdornment, StyledTitle } from "./styled";

export interface IComposition {
  token: IToken;
  metaFn: (...args: Array<any>) => Promise<any>;
}

export interface IErc998Composition {
  token: IToken;
}

export const Erc998Composition = (props: IErc998Composition) => {
  const { token } = props;

  const [action, setAction] = useState<IComposition>({} as IComposition);
  const [isComposeTokenDialogOpen, setIsComposeTokenDialogOpen] = useState(false);

  const { formatMessage } = useIntl();
  const { writeContractAsync } = useWriteContractWithSimulation();
  const { address: walletAddress = "" } = useAccount();

  // combine all function's abi
  const ERC998ABI = ([] as Array<any>)
    .concat(ERC721SimpleSafeTransferFrom721ABI)
    .concat(ERC998ERC1155ERC20EnumSafeTransferFromERC1155ABI)
    .concat(ERC998ERC1155ERC20EnumTransferERC20ABI)
    .concat(ERC998ERC1155ERC20EnumGetERC20ABI)
    .concat(ERC998SimpleSafeTransferChildABI)
    .concat(ERC1155SimpleSafeTransferFrom1155ABI);

  const { execute: metaComposeFn } = useMetamask(async (data: IToken, values: IComposeTokenDto) => {
    const contractType = data.template!.contract!.contractType;

    switch (contractType) {
      case TokenType.ERC20: // ERC20
        return writeContractAsync({
          address: data.template!.contract!.address as AddressType,
          abi: ERC998ABI,
          functionName: "getERC20",
          args: [walletAddress, token.tokenId, data.template!.contract!.address, values.amount],
        });

      case TokenType.ERC1155: // ERC1155
        return writeContractAsync({
          address: data.template!.contract!.address as AddressType,
          abi: ERC998ABI,
          functionName: "safeTransferFrom",
          args: [
            walletAddress,
            token.template!.contract!.address,
            data.tokenId,
            values.amount,
            pad(toHex(BigInt(token.tokenId)), { size: 32 }),
          ],
        });

      case TokenType.ERC721: // ERC721
      case TokenType.ERC998: // ERC998
        return writeContractAsync({
          address: data.template!.contract!.address as AddressType,
          abi: ERC998ABI,
          functionName: "safeTransferFrom",
          args: [
            walletAddress,
            token.template!.contract!.address,
            BigInt(data.tokenId),
            pad(toHex(BigInt(token.tokenId)), { size: 32 }),
          ],
        });
      default:
        return Promise.resolve("");
    }
  });

  const { execute: metaDecomposeFn } = useMetamask(async (data: IToken, values: IComposeTokenDto) => {
    const contractType = data.template!.contract!.contractType;

    switch (contractType) {
      case TokenType.ERC20: // ERC20
        return writeContractAsync({
          address: token.template!.contract!.address as AddressType,
          abi: ERC998ABI,
          functionName: "transferERC20",
          args: [token.tokenId, walletAddress, data.template!.contract!.address, values.amount],
        });
      case TokenType.ERC1155: // ERC1155
        return writeContractAsync({
          address: token.template!.contract!.address as AddressType,
          abi: ERC998ABI,
          functionName: "safeTransferFromERC1155",
          args: [token.tokenId, walletAddress, data.template!.contract!.address, data.tokenId, values.amount, "0x"],
        });
      case TokenType.ERC721: // ERC721
      case TokenType.ERC998: // ERC998
        return writeContractAsync({
          address: token.template!.contract!.address as AddressType,
          abi: ERC998ABI,
          functionName: "safeTransferChild",
          args: [
            token.tokenId,
            walletAddress,
            token.balance![0].token!.template!.contract!.address,
            token.balance![0].token?.tokenId!,
          ],
        });
      default:
        return Promise.resolve("");
    }
  });

  const handleComposeToken = (): void => {
    setIsComposeTokenDialogOpen(true);
  };

  const postponeAction = (token: IToken, metaFn: (...args: Array<any>) => Promise<any>) => {
    if (
      token.template!.contract!.contractType === TokenType.ERC20 ||
      token.template!.contract!.contractType === TokenType.ERC1155
    ) {
      setAction({ token, metaFn });
      handleComposeToken();
    } else {
      void metaFn(token, {});
    }
  };

  const handleComposeTokenConfirm = async (values: IComposeTokenDto): Promise<void> => {
    await action.metaFn(action.token, values).finally(() => {
      setIsComposeTokenDialogOpen(false);
    });
  };

  const handleComposeTokenCancel = (): void => {
    setIsComposeTokenDialogOpen(false);
  };

  const handleChange = (_event: ChangeEvent<unknown>, option: any): void => {
    postponeAction(option, metaComposeFn);
  };

  const handleAlert = (_event: ChangeEvent<unknown>, _option: any): void => {
    enqueueSnackbar(formatMessage({ id: "form.hints.compositionInactive" }), { variant: "error" });
  };

  const handleClear = (balance: IBalance) => () => {
    postponeAction(balance.token!, metaDecomposeFn);
  };

  if (!token.template?.contract?.children?.length) {
    return null;
  }

  return (
    <Fragment>
      <Typography variant="h4">Composed tokens</Typography>

      {token.template?.contract?.children?.map(child => {
        const active = child.compositionStatus === CompositionStatus.ACTIVE;
        const filtered = token.balance!.filter(balance => balance.token?.template?.contractId === child.childId); // token.children!.filter(ownership => ownership.child?.template?.contractId === child.childId);

        return (
          <FormWrapper
            key={child.id}
            initialValues={filtered.reduce(
              (memo, current, i) => {
                memo.tokenId[i] = formatTokenTitle(filtered[i].token!);
                return memo;
              },
              { tokenId: [] as Array<string> },
            )}
            onSubmit={Promise.resolve}
            showButtons={false}
            showPrompt={false}
            testId="TokenCompositionDialog"
          >
            <StyledTitle variant="h5">{child.child?.title}</StyledTitle>
            {new Array(filtered.length).fill(null).map((e, i) => (
              <StaticInput
                key={i}
                name={`tokenId[${i}]`}
                label={formatMessage({ id: "form.labels.tokenId" })}
                placeholder={formatMessage({ id: "form.placeholders.tokenId" })}
                slotProps={{
                  input: {
                    endAdornment: (
                      <StyledInputAdornment position="end">
                        <Button intent="icon" onClick={handleClear(filtered[i])} size="small">
                          <Clear fontSize="inherit" />
                        </Button>
                      </StyledInputAdornment>
                    ),
                  },
                }}
              />
            ))}
            {new Array(child.amount - filtered.length).fill(null).map((e, i) => (
              <EntityInput
                disableClear={true}
                key={i}
                name={`tokenId[${i + filtered.length}]`}
                controller="tokens"
                fetchData={{
                  contractIds: [child.child?.id],
                }}
                label={formatMessage({ id: "form.labels.tokenId" })}
                placeholder={formatMessage({ id: "form.placeholders.tokenId" })}
                getTitle={(token: IToken) => formatTokenTitle(token)}
                onChange={active ? handleChange : handleAlert}
              />
            ))}
          </FormWrapper>
        );
      })}

      <ComposeTokenDialog
        onCancel={handleComposeTokenCancel}
        onConfirm={handleComposeTokenConfirm}
        open={isComposeTokenDialogOpen}
        initialValues={{
          amount: "0",
          decimals: action.token?.template?.contract?.decimals || 0,
        }}
      />
    </Fragment>
  );
};
